/**
 * NewDatasetForm Component
 *
 * Form for registering a new dataset or editing an existing one. Collects
 * required metadata (title, description, data topic, files, publishability) and
 * optional metadata (permitted users, temporal/spatial dimensions and
 * resolutions), then submits to the appropriate API endpoint.
 *
 * In edit mode, non-editable fields (files, publishable, source email, contacts,
 * communication date) are shown but disabled.
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
  type JSX,
} from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { Plus, Mail, Upload, X, CalendarIcon, FileText } from "lucide-react"
import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useFileUpload } from "@/hooks/use-file-upload.ts"
import MultiSelectCombobox, {
  type ComboboxOption,
} from "@/components/shared/MultiSelectCombobox"
import {
  getDatasetMetadataDomains,
  getPermittedUsersOptions,
  createDataset,
  updateDataset,
  prettifyDomainValue,
  PERMITTED_USERS_ALL_SENTINEL,
  type Dataset,
  type DatasetMetadataDomains,
  type NewDatasetRequest,
  type UpdateDatasetRequest,
  type UserOption,
} from "@/services/datasetsService"
import { parseEmlFile, isEmlFile } from "@/utils/emailParser"

/** Props for the NewDatasetForm component. */
interface NewDatasetFormProps {
  /** Existing dataset to edit (if provided, form enters edit mode). */
  dataset?: Dataset
}

/**
 * Build combobox options where the label is the value verbatim.
 *
 * @param values - The values to wrap.
 * @returns Options with identical value and label.
 */
function toPlainOptions(values: string[]): ComboboxOption[] {
  return values.map((value) => ({ value, label: value }))
}

/**
 * Build a descending list of year strings from the current year back to a
 * floor, for the temporal dimension year selectors.
 *
 * @param earliest - The earliest year to include.
 * @returns Year strings, newest first.
 */
function buildYearOptions(earliest: number): string[] {
  const currentYear = new Date().getFullYear()
  const years: string[] = []
  for (let year = currentYear; year >= earliest; year--) {
    years.push(String(year))
  }
  return years
}

/** Year options for the temporal dimension selectors. */
const YEAR_OPTIONS: string[] = buildYearOptions(1980)

/**
 * Parse a temporal dimension string (e.g. "2000" or "2000-2020") into from/to years.
 *
 * @param temporalDimension - The temporal dimension string to parse.
 * @returns An object with fromYear and toYear strings (empty if not provided).
 */
function parseTemporalDimension(temporalDimension?: string): {
  fromYear: string
  toYear: string
} {
  if (!temporalDimension) {
    return { fromYear: "", toYear: "" }
  }
  if (temporalDimension.includes("-")) {
    const [from, to] = temporalDimension.split("-")
    return { fromYear: from, toYear: to }
  }
  // Single year — treat as both from and to
  return { fromYear: temporalDimension, toYear: "" }
}

/**
 * Form for creating a new dataset or editing an existing one.
 *
 * @param props - Component props.
 * @param props.dataset - Existing dataset to edit (optional, enables edit mode).
 * @returns The rendered dataset form.
 */
export default function NewDatasetForm({ dataset }: NewDatasetFormProps): JSX.Element {
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  /** Whether the form is in edit mode (has an existing dataset). */
  const isEditMode: boolean = dataset !== undefined

  // File picker — collects files for UX only; nothing is uploaded in the prototype.
  // Supports both a click-to-open dialog and drag-and-drop onto the drop zone.
  const [
    { files, isDragging },
    {
      openFileDialog,
      removeFile,
      getInputProps,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    },
  ] = useFileUpload({
    accept: "*",
    multiple: true,
  })

  // Email file upload — optional single .eml file attached as the
  // source email for provenance tracking.
  const [
    { files: emailFiles, isDragging: isEmailDragging },
    {
      openFileDialog: openEmailDialog,
      removeFile: removeEmailFile,
      getInputProps: getEmailInputProps,
      handleDragEnter: handleEmailDragEnter,
      handleDragLeave: handleEmailDragLeave,
      handleDragOver: handleEmailDragOver,
      handleDrop: handleEmailDrop,
    },
  ] = useFileUpload({
    accept: ".eml",
    multiple: false,
  })

  // Sharing agreement file upload — optional single file (any type) attached
  // as the formal sharing/usage agreement for the dataset.
  const [
    { files: sharingAgreementFiles, isDragging: isSharingAgreementDragging },
    {
      openFileDialog: openSharingAgreementDialog,
      removeFile: removeSharingAgreementFile,
      getInputProps: getSharingAgreementInputProps,
      handleDragEnter: handleSharingAgreementDragEnter,
      handleDragLeave: handleSharingAgreementDragLeave,
      handleDragOver: handleSharingAgreementDragOver,
      handleDrop: handleSharingAgreementDrop,
    },
  ] = useFileUpload({
    accept: "*",
    multiple: false,
  })

  // Metadata domains (stubbed) for the constrained inputs.
  const [domains, setDomains] = useState<DatasetMetadataDomains | null>(null)
  const [domainsError, setDomainsError] = useState<string | null>(null)

  // Candidate users for the permitted-users selector (stubbed API).
  const [userOptions, setUserOptions] = useState<UserOption[]>([])

  // Parse initial temporal dimension for edit mode.
  const initialTemporal = parseTemporalDimension(dataset?.temporalDimension)

  // Form state — required fields (pre-filled from dataset in edit mode)
  const [title, setTitle] = useState<string>(dataset?.title ?? "")
  const [description, setDescription] = useState<string>(dataset?.description ?? "")
  const [dataTopic, setDataTopic] = useState<string[]>(dataset?.dataTopic ?? [])

  // User-defined data topics added via the "Add new data topic" input.
  const [customDataTopics, setCustomDataTopics] = useState<string[]>([])
  // Controls whether the inline "add" input is visible.
  const [showAddDataTopic, setShowAddDataTopic] = useState<boolean>(false)
  // The value currently being typed in the "add" input.
  const [newDataTopicValue, setNewDataTopicValue] = useState<string>("")

  // Form state — optional fields (pre-filled from dataset in edit mode)
  const [permittedUsers, setPermittedUsers] = useState<string[]>(
    // Exclude the __ALL__ sentinel from the user-picker state — it's managed
    // by the toggle below.
    () => (dataset?.permittedUsers ?? []).filter((u) => u !== PERMITTED_USERS_ALL_SENTINEL)
  )
  // "Share with all users" toggle — derived from the sentinel.
  const [shareWithAll, setShareWithAll] = useState<boolean>(
    () => (dataset?.permittedUsers ?? []).includes(PERMITTED_USERS_ALL_SENTINEL)
  )
  const [temporalFromYear, setTemporalFromYear] = useState<string>(initialTemporal.fromYear)
  const [temporalToYear, setTemporalToYear] = useState<string>(initialTemporal.toYear)
  const [temporalResolution, setTemporalResolution] = useState<string[]>(dataset?.temporalResolution ?? [])
  const [countries, setCountries] = useState<string[]>(dataset?.countries ?? [])
  const [spatialAggregationLevel, setSpatialAggregationLevel] = useState<string[]>(dataset?.spatialAggregationLevel ?? [])

  // Unit of analysis — checkbox group with an "Other" toggle that reveals a
  // free-form input. The seed values come from the domain (Child, Woman, …);
  // anything typed into the "Other" input is submitted verbatim alongside the
  // checked seed values.
  //
  // We store three slices of state instead of a single `string[]` so that:
  //   - toggling "Other" off without losing the typed value is possible
  //   - we can correctly initialise from a dataset's existing list once the
  //     domain seed list has loaded (split into seed-checkboxes vs other-input)
  //
  // The submitted array is derived from these three values at submit time.
  const [unitOfAnalysisSelected, setUnitOfAnalysisSelected] = useState<string[]>([])
  const [unitOfAnalysisOtherChecked, setUnitOfAnalysisOtherChecked] = useState<boolean>(false)
  const [unitOfAnalysisOther, setUnitOfAnalysisOther] = useState<string>("")
  // Tracks whether we've already initialised the unit-of-analysis state from
  // the dataset + domain seed list. Without this guard, the initialisation
  // effect would clobber the user's edits whenever `domains` changes.
  const [unitOfAnalysisInitialized, setUnitOfAnalysisInitialized] = useState<boolean>(false)

  // Form state — Source (biblio) fields
  // Default publishable to the most restrictive status so sensitive received
  // data is never accidentally marked releasable.
  // In edit mode, these are read-only and pre-filled from the dataset.
  const [publishable, setPublishable] = useState<string>(dataset?.publishable ?? "CONFIDENTIAL")

  // Sharing agreement text — optional free-form description of usage permissions.
  // Editable in both create and edit mode.
  const [sharingAgreementText, setSharingAgreementText] = useState<string>(
    dataset?.sharingAgreementText ?? ""
  )
  // Contacts list: array of name strings. Starts with one empty entry.
  const [biblioContacts, setBiblioContacts] = useState<string[]>([""])
  const [biblioCommunicationDate, setBiblioCommunicationDate] = useState<Date | undefined>(undefined)

  // Submission state
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  // Parse email file when attached to pre-fill contacts and date (create mode only).
  useEffect(() => {
    // Skip email parsing in edit mode — source fields are read-only.
    if (isEditMode) return

    async function parseEmail(): Promise<void> {
      if (emailFiles.length === 0) return

      const emailFile = emailFiles[0].file
      if (!(emailFile instanceof File)) return

      // Only parse .eml files (skip .msg for now)
      if (!isEmlFile(emailFile)) {
        console.warn("Email parsing only supported for .eml files")
        return
      }

      try {
        const parsed = await parseEmlFile(emailFile)

        // Pre-fill sender name if we got one and contacts are currently empty/default
        if (parsed.senderName) {
          setBiblioContacts((current) => {
            // Only pre-fill if contacts are in the default state (single empty entry)
            if (current.length === 1 && current[0] === "") {
              return [parsed.senderName]
            }
            return current
          })
        }

        // Pre-fill date if we got one and date is not already set
        if (parsed.date) {
          setBiblioCommunicationDate((current) => {
            // Only pre-fill if date is not already set
            if (current === undefined) {
              return parsed.date
            }
            return current
          })
        }
      } catch (err) {
        console.error("Failed to parse email file:", err)
        // Don't show an error to the user — parsing is a convenience, not required
      }
    }

    parseEmail()
  }, [emailFiles, isEditMode])

  // Load the metadata domains and candidate users on mount.
  useEffect(() => {
    async function loadOptions(): Promise<void> {
      try {
        const accessToken = await getAccessToken()
        const [domainsResult, usersResult] = await Promise.all([
          getDatasetMetadataDomains(accessToken),
          getPermittedUsersOptions(accessToken),
        ])
        setDomains(domainsResult)
        setUserOptions(usersResult)
      } catch (err) {
        setDomainsError(
          err instanceof Error ? err.message : "Failed to load metadata options"
        )
      }
    }
    loadOptions()
  }, [getAccessToken])

  /**
   * Initialise the unit-of-analysis state from the dataset (in edit mode)
   * once the domain seed list is available.
   *
   * Splits the dataset's stored ``unitOfAnalysis`` list into:
   *   - values present in the seed list  → checkbox-selected seeds
   *   - values not in the seed list      → the "Other" input value (joined
   *                                         with ", " on the rare chance
   *                                         that more than one custom value
   *                                         was stored historically)
   *
   * Runs at most once per mount, guarded by ``unitOfAnalysisInitialized``,
   * to avoid clobbering subsequent user edits if ``domains`` re-renders.
   */
  useEffect(() => {
    if (unitOfAnalysisInitialized) return
    if (!domains) return

    const seedSet = new Set<string>(domains.unitOfAnalysis ?? [])
    const initialValues: string[] = dataset?.unitOfAnalysis ?? []
    const seedSelected: string[] = []
    const customValues: string[] = []
    for (const value of initialValues) {
      if (seedSet.has(value)) {
        seedSelected.push(value)
      } else {
        customValues.push(value)
      }
    }
    setUnitOfAnalysisSelected(seedSelected)
    if (customValues.length > 0) {
      setUnitOfAnalysisOtherChecked(true)
      setUnitOfAnalysisOther(customValues.join(", "))
    }
    setUnitOfAnalysisInitialized(true)
  }, [domains, dataset, unitOfAnalysisInitialized])

  /**
   * Derived list of permitted users to send to the backend. When the "Share
   * with all users" toggle is on, the sentinel ``__ALL__`` is prepended so the
   * backend grants access to every authenticated user.
   */
  const permittedUsersToSubmit: string[] = useMemo(() => {
    if (shareWithAll) {
      // When sharing with all, individual subs become irrelevant for access,
      // but we still send them so the owner can toggle the switch off later
      // and retain the individual selections without re-picking.
      return [PERMITTED_USERS_ALL_SENTINEL, ...permittedUsers]
    }
    return permittedUsers
  }, [shareWithAll, permittedUsers])

  /**
   * Derived list of unit-of-analysis values to send to the backend. Combines
   * the checked seed values with the "Other" free-form input (when the
   * Other checkbox is on and the input is non-empty). The literal label
   * "Other" is intentionally never submitted — only the user's typed value.
   */
  const unitOfAnalysisToSubmit: string[] = useMemo(() => {
    const result: string[] = [...unitOfAnalysisSelected]
    const trimmedOther: string = unitOfAnalysisOther.trim()
    if (unitOfAnalysisOtherChecked && trimmedOther.length > 0) {
      result.push(trimmedOther)
    }
    return result
  }, [unitOfAnalysisSelected, unitOfAnalysisOtherChecked, unitOfAnalysisOther])

  /**
   * Toggle a value within a multi-select list state setter (add if absent,
   * remove if present). Shared by the temporal and spatial resolution inputs.
   *
   * @param setter - The state setter for the multi-select list.
   * @param value - The value to add or remove.
   */
  const toggleMultiValue = useCallback(
    (setter: Dispatch<SetStateAction<string[]>>, value: string): void => {
      setter((current) =>
        current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value]
      )
    },
    []
  )

  /**
   * Year options for the end-year selector. When a start year is selected,
   * exclude years that are not strictly after the start year — equal years
   * are disallowed because a single-year dataset is expressed by leaving
   * the end year blank, not by picking the same year twice.
   */
  const toYearOptions: string[] = useMemo(() => {
    if (!temporalFromYear) return YEAR_OPTIONS
    const fromInt: number = Number(temporalFromYear)
    return YEAR_OPTIONS.filter((year) => Number(year) > fromInt)
  }, [temporalFromYear])

  /**
   * Inline validation error for the temporal year range. Used to surface a
   * message under the year selectors and to block form submission. The
   * dropdown filter normally keeps end > start, so this is mainly defence
   * in depth (e.g. edit mode loading legacy data with end <= start).
   */
  const temporalRangeError: string | null = useMemo(() => {
    if (!temporalFromYear || !temporalToYear) return null
    if (Number(temporalToYear) <= Number(temporalFromYear)) {
      return "End year must be after start year. Leave it blank for a single-year dataset."
    }
    return null
  }, [temporalFromYear, temporalToYear])

  /**
   * Update the temporal start year. If the newly selected start year is
   * not strictly less than the currently selected end year, clear the end
   * year so the form never retains an invalid (or redundant) range
   * internally.
   *
   * @param value - The newly selected start year (as a string).
   */
  const handleFromYearChange = useCallback((value: string): void => {
    setTemporalFromYear(value)
    // Clear a now-invalid or redundant end year. Setting to "" makes the
    // placeholder reappear; the user can pick a valid end year again.
    if (temporalToYear && Number(value) >= Number(temporalToYear)) {
      setTemporalToYear("")
    }
  }, [temporalToYear])

  /**
   * All available data topic options: the domain-supplied values merged with
   * any user-added custom values (deduped).
   */
  const allDataTopicOptions: ComboboxOption[] = useMemo(() => {
    const domainValues = domains?.dataTopic ?? []
    const merged = [...domainValues, ...customDataTopics.filter(
      (ct) => !domainValues.includes(ct)
    )]
    return toPlainOptions(merged)
  }, [domains, customDataTopics])

  /**
   * Confirm and add a custom data topic. Trims whitespace, rejects blanks and
   * duplicates, then adds the value to both the custom topics list and the
   * current selection.
   */
  const handleAddCustomDataTopic = useCallback((): void => {
    const trimmed = newDataTopicValue.trim()
    if (!trimmed) return

    const alreadyExists =
      (domains?.dataTopic ?? []).includes(trimmed) ||
      customDataTopics.includes(trimmed)

    if (!alreadyExists) {
      setCustomDataTopics((prev) => [...prev, trimmed])
    }

    // Auto-select the newly added topic if not already selected.
    if (!dataTopic.includes(trimmed)) {
      setDataTopic((prev) => [...prev, trimmed])
    }

    setNewDataTopicValue("")
    setShowAddDataTopic(false)
  }, [newDataTopicValue, domains, customDataTopics, dataTopic])

  /**
   * Validate and submit the form. In create mode, builds the request payload
   * and creates a new dataset. In edit mode, sends only updated fields.
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    setSubmitError(null)
    setSubmitSuccess(null)

    // Required-field validation (mirrors the intended backend contract).
    if (!title.trim()) {
      setSubmitError("Title is required.")
      return
    }
    if (dataTopic.length === 0) {
      setSubmitError("At least one data topic is required.")
      return
    }

    // In create mode, also validate files and publishable.
    if (!isEditMode) {
      if (!publishable) {
        setSubmitError("Publishable status is required.")
        return
      }
      if (files.length === 0) {
        setSubmitError("At least one file is required.")
        return
      }
    }

    // Derive the temporal dimension string from the year selectors:
    //   - both years    → "from-to" (e.g. "2000-2020"); end must be strictly
    //                     after start (single-year datasets use the from year
    //                     alone, with the end-year dropdown left blank)
    //   - only the from  → single year (e.g. "2000")
    //   - neither        → omitted
    let temporalDimension = ""
    if (temporalToYear && !temporalFromYear) {
      setSubmitError("Select a start year before an end year.")
      return
    }
    if (temporalFromYear && temporalToYear) {
      if (Number(temporalToYear) <= Number(temporalFromYear)) {
        setSubmitError(
          "End year must be after start year. Leave it blank for a single-year dataset."
        )
        return
      }
      temporalDimension = `${temporalFromYear}-${temporalToYear}`
    } else if (temporalFromYear) {
      temporalDimension = temporalFromYear
    }

    setSubmitting(true)
    try {
      const accessToken = await getAccessToken()

      if (isEditMode) {
        // Edit mode: send only the updatable fields.
        //
        // Every optional field is included unconditionally — even when blank
        // or empty — so the user can clear a previously-set value. Omitting
        // a field tells the backend "leave alone", which would prevent the
        // removal from sticking. (Required fields like title and dataTopic
        // are always non-empty here because the validation above rejected
        // empty submissions.)
        const updates: UpdateDatasetRequest = {
          title: title.trim(),
          dataTopic,
          description: description.trim(),
          permittedUsers: permittedUsersToSubmit,
          temporalDimension,
          temporalResolution,
          countries,
          spatialAggregationLevel,
          sharingAgreementText: sharingAgreementText.trim(),
        }

        // Guarded — see the unitOfAnalysisToSubmit useMemo for why.
        if (unitOfAnalysisInitialized) {
          updates.unitOfAnalysis = unitOfAnalysisToSubmit
        }

        const response = await updateDataset(accessToken, dataset!.datasetId, updates)
        // Navigate back to the datasets list with the edited dataset
        // selected — DatasetsInterface reads ?datasetId=… from the URL and
        // opens its details panel automatically.
        navigate(`/datasets?datasetId=${response.datasetId}`)
      } else {
        // Create mode: full request with files and biblio fields.
        const request: NewDatasetRequest = {
          title: title.trim(),
          dataTopic,
          publishable,
          fileKeys: files.map((f) => f.file.name),
        }

        // Attach optional fields only when provided.
        if (description.trim()) {
          request.description = description.trim()
        }
        if (permittedUsersToSubmit.length > 0) {
          request.permittedUsers = permittedUsersToSubmit
        }
        if (temporalDimension) {
          request.temporalDimension = temporalDimension
        }
        if (temporalResolution.length > 0) {
          request.temporalResolution = temporalResolution
        }
        if (countries.length > 0) {
          request.countries = countries
        }
        if (spatialAggregationLevel.length > 0) {
          request.spatialAggregationLevel = spatialAggregationLevel
        }
        if (unitOfAnalysisToSubmit.length > 0) {
          request.unitOfAnalysis = unitOfAnalysisToSubmit
        }

        // Sharing agreement fields.
        if (sharingAgreementFiles.length > 0) {
          request.sharingAgreementFileKeys = sharingAgreementFiles.map((f) => f.file.name)
        }
        if (sharingAgreementText.trim()) {
          request.sharingAgreementText = sharingAgreementText.trim()
        }

        // Source (biblio) optional fields.
        // Filter out empty contacts and only include if there's at least one non-empty contact.
        const nonEmptyContacts = biblioContacts.filter((name) => name.trim())
        if (nonEmptyContacts.length > 0) {
          request.biblioContacts = nonEmptyContacts
        }
        if (biblioCommunicationDate) {
          request.biblioCommunicationDate = format(biblioCommunicationDate, "yyyy-MM-dd")
        }

        const fileObjects: File[] = files.map((f) => f.file as File)
        // Extract the email file if attached (for biblio)
        const emailFile: File | undefined = emailFiles.length > 0
          ? (emailFiles[0].file as File)
          : undefined
        // Extract sharing agreement files (if attached)
        const saFiles: File[] | undefined = sharingAgreementFiles.length > 0
          ? sharingAgreementFiles.map((f) => f.file as File)
          : undefined
        const response = await createDataset(accessToken, request, fileObjects, emailFile, saFiles)
        // Navigate back to the datasets list with the newly-created dataset
        // selected — DatasetsInterface reads ?datasetId=… from the URL and
        // opens its details panel automatically.
        navigate(`/datasets?datasetId=${response.datasetId}`)
      }
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : `Failed to ${isEditMode ? "update" : "create"} dataset`
      )
    } finally {
      setSubmitting(false)
    }
  }, [
    title,
    description,
    dataTopic,
    publishable,
    files,
    permittedUsersToSubmit,
    temporalFromYear,
    temporalToYear,
    temporalResolution,
    countries,
    spatialAggregationLevel,
    unitOfAnalysisToSubmit,
    unitOfAnalysisInitialized,
    sharingAgreementFiles,
    sharingAgreementText,
    biblioContacts,
    biblioCommunicationDate,
    isEditMode,
    dataset,
    emailFiles,
    getAccessToken,
    navigate,
  ])

  return (
    <>
    <title>{isEditMode ? "Edit Dataset" : "New Dataset"} - MAP Apps Platform</title>
    <div className="mx-auto w-full max-w-2xl p-6">
      <Card>
        <CardHeader>
          <CardTitle><h1>{isEditMode ? "Edit Dataset" : "Create a new Dataset"}</h1></CardTitle>
          <CardDescription>
            {isEditMode
              ? "Update the dataset metadata. Fields marked as read-only cannot be changed."
              : "Register a received dataset and its metadata. Required fields are marked with *."}
          </CardDescription>
        </CardHeader>

        <CardContent className="grid gap-4">
          {/* Title — required */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-title" className="text-sm font-medium">
              Title *
            </label>
            <Input
              id="dataset-title"
              placeholder="e.g. Nigeria ITN distribution 2023"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Data Topic — required, multi-select searchable */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-data-topic" className="text-sm font-medium">
              Data Topic *
            </label>
            <MultiSelectCombobox
              id="dataset-data-topic"
              options={allDataTopicOptions}
              selected={dataTopic}
              onChange={setDataTopic}
              placeholder="Select data topic(s)"
            />
            {/* Add new data topic — inline input toggled by button */}
            {!showAddDataTopic ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="justify-start w-fit gap-1 px-1 text-muted-foreground"
                onClick={() => setShowAddDataTopic(true)}
              >
                <Plus className="size-4" />
                Add new data topic
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  autoFocus
                  placeholder="New data topic name"
                  value={newDataTopicValue}
                  onChange={(e) => setNewDataTopicValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddCustomDataTopic()
                    }
                    if (e.key === "Escape") {
                      setShowAddDataTopic(false)
                      setNewDataTopicValue("")
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddCustomDataTopic}
                  disabled={!newDataTopicValue.trim()}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddDataTopic(false)
                    setNewDataTopicValue("")
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Files — required in create mode, read-only in edit mode */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-files" className="text-sm font-medium">
              Files {isEditMode ? "(read-only)" : "*"}
            </label>
            <div className="space-y-2">
              {isEditMode ? (
                // Edit mode: show read-only file list from the dataset.
                <ul className="space-y-1 rounded-md border border-input bg-muted/50 p-3">
                  {(dataset!.versions[String(dataset!.currentVersion)]?.fileKeys ?? []).map((fileKey) => (
                    <li key={fileKey} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Upload className="size-4 shrink-0" />
                      <span className="truncate">{fileKey}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                // Create mode: interactive file upload.
                <>
                  {/* Drop zone — click to open dialog or drag files onto it. */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={openFileDialog}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        openFileDialog()
                      }
                    }}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    data-dragging={isDragging || undefined}
                    className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input p-6 text-center cursor-pointer transition-colors hover:bg-accent/50 data-[dragging=true]:border-primary data-[dragging=true]:bg-accent"
                  >
                    <Upload className="size-5 text-muted-foreground" />
                    <p className="text-sm">
                      <span className="font-medium">Choose file(s)</span> or drag and
                      drop here
                    </p>
                  </div>
                  <input {...getInputProps()} className="sr-only" />
                  {files.length > 0 && (
                    <ul className="space-y-1">
                      {files.map((file) => (
                        <li
                          key={file.id}
                          className="flex items-center gap-2 text-sm"
                        >
                          <span className="truncate">{file.file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="size-5 p-0"
                            onClick={() => removeFile(file.id)}
                            aria-label={`Remove ${file.file.name}`}
                          >
                            <X className="size-3" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Description — optional */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-description" className="text-sm font-medium">
              Description
            </label>
            <Textarea
              id="dataset-description"
              placeholder="What does this dataset contain, and where did it come from?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* ------------------------------------------------------------- */}
          {/* Sharing Permissions                                           */}
          {/* ------------------------------------------------------------- */}
          <div className="border-t pt-4">
            <h2 className="text-base font-semibold">Sharing Permissions</h2>
            <p className="text-sm text-muted-foreground">
              Permissions and restrictions within MAP and publicly
            </p>
          </div>

          {/* Permitted MAP Users — optional, multi-select searchable */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-permitted-users" className="text-sm font-medium">
              Permitted MAP Users
            </label>
            {/* "Share with all" toggle — when checked, the multi-select is
                disabled but preserved (so unchecking restores the previous
                individual selections). */}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={shareWithAll}
                onChange={(e) => setShareWithAll(e.target.checked)}
              />
              Share with all users
            </label>
            <MultiSelectCombobox
              id="dataset-permitted-users"
              options={userOptions.map((u) => ({
                value: u.sub,
                label: u.name,
              }))}
              selected={permittedUsers}
              onChange={setPermittedUsers}
              placeholder={shareWithAll ? "All users have access" : "Select users"}
              disabled={shareWithAll}
            />
            <p className="text-xs text-muted-foreground">
              {shareWithAll
                ? "All authenticated users will have read access."
                : "Users granted read access in addition to you (the owner)."}
            </p>
          </div>

          {/* Publishable — required in create mode, read-only in edit mode */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-publishable" className="text-sm font-medium">
              Publishable {isEditMode ? "(read-only)" : "*"}
            </label>
            <Select value={publishable} onValueChange={setPublishable} disabled={isEditMode}>
              <SelectTrigger id="dataset-publishable" className="w-full">
                <SelectValue placeholder="Select a release status" />
              </SelectTrigger>
              <SelectContent>
                {domains?.publishable.map((value) => (
                  <SelectItem key={value} value={value}>
                    {prettifyDomainValue(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Whether this data is cleared to be published publicly.
            </p>
          </div>

          {/* Sharing Agreement File — optional, single file, read-only in edit mode */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-sharing-agreement-file" className="text-sm font-medium">
              Data Sharing Agreement File {isEditMode ? "(read-only)" : ""}
            </label>
            <div className="space-y-2">
              {isEditMode ? (
                // Edit mode: show read-only file list from the dataset (if any).
                dataset?.sharingAgreementFileKeys && dataset.sharingAgreementFileKeys.length > 0 ? (
                  <ul className="space-y-1 rounded-md border border-input bg-muted/50 p-3">
                    {dataset.sharingAgreementFileKeys.map((fileKey) => (
                      <li key={fileKey} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <FileText className="size-4 shrink-0" />
                        <span className="truncate">{fileKey}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No sharing agreement file attached.</p>
                )
              ) : (
                // Create mode: interactive file upload.
                <>
                  {sharingAgreementFiles.length === 0 ? (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={openSharingAgreementDialog}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          openSharingAgreementDialog()
                        }
                      }}
                      onDragEnter={handleSharingAgreementDragEnter}
                      onDragLeave={handleSharingAgreementDragLeave}
                      onDragOver={handleSharingAgreementDragOver}
                      onDrop={handleSharingAgreementDrop}
                      data-dragging={isSharingAgreementDragging || undefined}
                      className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input p-4 text-center cursor-pointer transition-colors hover:bg-accent/50 data-[dragging=true]:border-primary data-[dragging=true]:bg-accent"
                    >
                      <FileText className="size-5 text-muted-foreground" />
                      <p className="text-sm">
                        <span className="font-medium">Attach sharing agreement</span>{" "}
                        (any file type)
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm rounded-md border px-3 py-2">
                      <FileText className="size-4 text-muted-foreground shrink-0" />
                      <span className="truncate">{sharingAgreementFiles[0].file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="size-5 p-0 ml-auto"
                        onClick={() => removeSharingAgreementFile(sharingAgreementFiles[0].id)}
                        aria-label={`Remove ${sharingAgreementFiles[0].file.name}`}
                      >
                        <X className="size-3" />
                      </Button>
                    </div>
                  )}
                  <input {...getSharingAgreementInputProps()} id="dataset-sharing-agreement-file" className="sr-only" />
                </>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            If available, include the data sharing agreement for this dataset
          </p>

          {/* Sharing Agreement Text — optional free-form, editable in both modes */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-sharing-agreement-text" className="text-sm font-medium">
              Usage Permissions
            </label>
            <Textarea
              id="dataset-sharing-agreement-text"
              placeholder="Describe any usage restrictions or permissions for this dataset"
              value={sharingAgreementText}
              onChange={(e) => setSharingAgreementText(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Description of how this dataset may be used, shared, or cited outside of MAP.
            </p>
          </div>

          {/* ------------------------------------------------------------- */}
          {/* Metadata                                                      */}
          {/* ------------------------------------------------------------- */}
          <div className="border-t pt-4">
            <h2 className="text-base font-semibold">Metadata</h2>
            <p className="text-sm text-muted-foreground">
              Optional metadata to facilitate searching and identifying
            </p>
          </div>

          {/* Temporal Dimension — optional, year range */}
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">Temporal Dimension</span>
            <div className="flex items-center gap-2">
              {/* Start year — wrapped with an inline clear (X) button so the
                  user can return to the empty/placeholder state. */}
              <div className="flex flex-1 items-center gap-1">
                <Select value={temporalFromYear} onValueChange={handleFromYearChange}>
                  <SelectTrigger id="dataset-temporal-from" className="w-full">
                    <SelectValue placeholder="From year" />
                  </SelectTrigger>
                  <SelectContent>
                    {YEAR_OPTIONS.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {temporalFromYear && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="size-8 shrink-0 p-0"
                    // Clear both years: an end year without a start year is
                    // not a valid temporal dimension, so we drop both rather
                    // than leaving a dangling end selection.
                    onClick={() => {
                      setTemporalFromYear("")
                      setTemporalToYear("")
                    }}
                    aria-label="Clear start year"
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
              <span className="text-sm text-muted-foreground">to</span>
              {/* End year — wrapped with an inline clear (X) button. The
                  end-year list is filtered to > start year so an invalid
                  range cannot be chosen from the dropdown. */}
              <div className="flex flex-1 items-center gap-1">
                <Select value={temporalToYear} onValueChange={setTemporalToYear}>
                  <SelectTrigger id="dataset-temporal-to" className="w-full">
                    <SelectValue placeholder="End year (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {toYearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {temporalToYear && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="size-8 shrink-0 p-0"
                    onClick={() => setTemporalToYear("")}
                    aria-label="Clear end year"
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Pick a single year (leave the end blank), or a start and end
              year for a range.
            </p>
            {temporalRangeError && (
              <p className="text-xs text-destructive" role="alert">
                {temporalRangeError}
              </p>
            )}
          </div>

          {/* Temporal Resolution — optional, multi-select */}
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">Temporal Resolution</span>
            <div className="flex flex-wrap gap-3">
              {domains?.temporalResolution.map((value) => (
                <label key={value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={temporalResolution.includes(value)}
                    onChange={() => toggleMultiValue(setTemporalResolution, value)}
                  />
                  {prettifyDomainValue(value)}
                </label>
              ))}
            </div>
          </div>

          {/* Countries — optional, multi-select searchable */}
          <div className="grid gap-1.5">
            <label htmlFor="dataset-countries" className="text-sm font-medium">
              Countries
            </label>
            <MultiSelectCombobox
              id="dataset-countries"
              options={(domains?.countries ?? []).map((c) => ({
                value: c.iso3,
                label: c.countryName,
              }))}
              selected={countries}
              onChange={setCountries}
              placeholder="Select countries"
            />
          </div>

          {/* Level of Spatial Aggregation — optional, multi-select */}
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">Level of Spatial Aggregation</span>
            <div className="flex flex-wrap gap-3">
              {domains?.spatialAggregationLevel?.map((value) => (
                <label
                  key={value}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={spatialAggregationLevel.includes(value)}
                    onChange={() => toggleMultiValue(setSpatialAggregationLevel, value)}
                  />
                  {prettifyDomainValue(value)}
                </label>
              ))}
            </div>
          </div>

          {/* Unit of Analysis — optional, multi-select with an "Other"
              checkbox that reveals a free-form input. The submitted list
              contains the checked seed values plus the typed-in custom value
              (the literal "Other" label is never submitted). */}
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">Unit of Analysis</span>
            <div className="flex flex-wrap gap-3">
              {domains?.unitOfAnalysis?.map((value) => (
                <label
                  key={value}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={unitOfAnalysisSelected.includes(value)}
                    onChange={() => toggleMultiValue(setUnitOfAnalysisSelected, value)}
                  />
                  {value}
                </label>
              ))}
              {/* "Other" — a separate checkbox not backed by a domain value.
                  When checked, the input below is shown; the user's typed
                  value is what gets submitted. */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={unitOfAnalysisOtherChecked}
                  onChange={(e) => setUnitOfAnalysisOtherChecked(e.target.checked)}
                />
                Other
              </label>
            </div>
            {unitOfAnalysisOtherChecked && (
              <Input
                placeholder="Specify other unit of analysis"
                value={unitOfAnalysisOther}
                onChange={(e) => setUnitOfAnalysisOther(e.target.value)}
                aria-label="Other unit of analysis"
              />
            )}
          </div>

          {/* ------------------------------------------------------------- */}
          {/* Source (biblio) section                                       */}
          {/* ------------------------------------------------------------- */}
          <div className="border-t pt-4">
            <h2 className="text-base font-semibold">Source {isEditMode && "(read-only)"}</h2>
            <p className="text-sm text-muted-foreground">
              {isEditMode
                ? "Source information cannot currently be edited. "
                : "Where this data came from. Recorded against the biblio source record."}
            </p>
          </div>

          {/* Source Email — optional, single .eml file, hidden in edit mode */}
          {!isEditMode && (
            <div className="grid gap-1.5">
              <label htmlFor="dataset-source-email" className="text-sm font-medium">
                Source Email
              </label>
              <div className="space-y-2">
                {emailFiles.length === 0 ? (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={openEmailDialog}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        openEmailDialog()
                      }
                    }}
                    onDragEnter={handleEmailDragEnter}
                    onDragLeave={handleEmailDragLeave}
                    onDragOver={handleEmailDragOver}
                    onDrop={handleEmailDrop}
                    data-dragging={isEmailDragging || undefined}
                    className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input p-4 text-center cursor-pointer transition-colors hover:bg-accent/50 data-[dragging=true]:border-primary data-[dragging=true]:bg-accent"
                  >
                    <Mail className="size-5 text-muted-foreground" />
                    <p className="text-sm">
                      <span className="font-medium">Attach saved email</span>{" "}
                      (.eml file)
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm rounded-md border px-3 py-2">
                    <Mail className="size-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{emailFiles[0].file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="size-5 p-0 ml-auto"
                      onClick={() => removeEmailFile(emailFiles[0].id)}
                      aria-label={`Remove ${emailFiles[0].file.name}`}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                )}
                <input {...getEmailInputProps()} id="dataset-source-email" className="sr-only" />
                <p className="text-xs text-muted-foreground">
                  Optionally attach the email that delivered this dataset for
                  provenance tracking.
                </p>
              </div>
            </div>
          )}

          {/* Contacts — optional (biblio), multiple names, hidden in edit mode */}
          {!isEditMode && (
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Contacts</span>
              <div className="space-y-2">
                {biblioContacts.map((contact, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Contact name"
                      value={contact}
                      onChange={(e) => {
                        const updated = [...biblioContacts]
                        updated[index] = e.target.value
                        setBiblioContacts(updated)
                      }}
                      className="flex-1"
                    />
                    {biblioContacts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="size-8 p-0"
                        onClick={() => {
                          setBiblioContacts(biblioContacts.filter((_, i) => i !== index))
                        }}
                        aria-label="Remove contact"
                      >
                        <X className="size-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="justify-start w-fit gap-1 px-1 text-muted-foreground"
                  onClick={() => setBiblioContacts([...biblioContacts, ""])}
                >
                  <Plus className="size-4" />
                  Add contact
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Name(s) of the person(s) who sent this dataset.
              </p>
            </div>
          )}

          {/* Communication Date — optional (biblio), hidden in edit mode */}
          {!isEditMode && (
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Communication Date</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="dataset-communication-date"
                    variant="outline"
                    data-empty={!biblioCommunicationDate}
                    className="w-full justify-start text-left font-normal data-[empty=true]:text-muted-foreground"
                  >
                    <CalendarIcon className="size-4" />
                    {biblioCommunicationDate
                      ? format(biblioCommunicationDate, "dd/MM/yyyy")
                      : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    captionLayout="dropdown"
                    selected={biblioCommunicationDate}
                    onSelect={setBiblioCommunicationDate}
                    disabled={(date) => date > new Date()}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                The date the dataset was received.
              </p>
            </div>
          )}

          {/* Domains load error */}
          {domainsError && (
            <p className="text-sm text-destructive" role="alert">
              {domainsError}
            </p>
          )}

          {/* Submit error / success */}
          {submitError && (
            <p className="text-sm text-destructive" role="alert">
              {submitError}
            </p>
          )}
          {submitSuccess && (
            <p className="text-sm text-green-600" role="status">
              {submitSuccess}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => navigate(isEditMode ? `/datasets/${dataset!.datasetId}` : "/datasets")}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || temporalRangeError !== null}>
              {submitting
                ? (isEditMode ? "Saving…" : "Creating…")
                : (isEditMode ? "Save Changes" : "Create Dataset")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  )
}
